import { useAuthState } from "@/contexts/auth/auth.provider";
import { useMutation } from "@apollo/client";
import { REPORT_IMAGES_ISSUE } from "@/graphql/mutations/report";
import React, { useRef } from "react";
import { useFavListingsState } from "@/contexts/fav-listings/favListings.provider";
import { usePath } from "@/contexts/path/path";
import {
  addImgReportedID,
  CloudfrontImageLoader,
  financialFormat,
  getSpecs,
  getStoredImgsReportedIDs,
} from "@/lib/helpers";
import css from "./.module.scss";
import Link from "next/link";
import Image from "next/image";
import Icon from "@/components/icon";

export const getImageSrc = (images) =>
  Array.isArray(images) && images[0] ? images[0] : "";
export function DailyListingCard({
  listing,
  onClick,
  index,
  imageClass,
  preloadImage,
  horizontal,
  trackClickEvent,
}) {
  const {
    id,
    price,
    sov_campaign_id,
    range_price,
    original_range_price,
    title,
    district,
    content,
    city,
    special,
  } = listing || {};

  const token = useAuthState("token");

  const [reportImagesIssue] = useMutation(REPORT_IMAGES_ISSUE, {
    context: { token },
  });
  const image = getImageSrc(listing.imgs);
  const imageWrapper = useRef();

  const isFavorite = useFavListingsState(listing.id);
  const { updateContext, daily_renting_filter } = usePath();
  const specs = getSpecs(listing);

  const minDays = listing.extended_details?.minimum_booking_days || 0;
  let period = "يومي";

  if (minDays >= 7 && minDays < 28) {
    period = "أسبوعي";
  } else if (minDays >= 28) {
    period = "شهري";
  }

  return (
    <div className={css.link}>
      <Link passHref href={listing.path || ""} scroll>
        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (onClick) return onClick(e, index);
            trackClickEvent && trackClickEvent();
            return updateContext({
              currentFrozenIndex: index,
              dailyData: daily_renting_filter?.day_date
                ? {
                    price: {
                      total_price: listing.original_range_price || 0,
                      discounted_price: listing.range_price || 0,
                      discount_reason: listing.daily_renting_discount_reason,
                    },
                  }
                : undefined,
              listing: {
                ...listing,
                loadedImage: imageWrapper.current?.firstChild?.firstChild
                  ?.complete
                  ? imageWrapper.current.firstChild?.firstChild.currentSrc
                  : undefined,
              },
            });
          }}
          className={css.listingCard + " " + (horizontal ? css.horizontal : "")}
        >
          <div className={css.absolute}>
            {(special || sov_campaign_id) && (
              <div
                className={
                  css.sov /*sov_campaign_id ? css.sov : css.specialText*/
                }
              >
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                    />
                  </svg>
                </span>
                <p> مميز</p>
              </div>
            )}

            {isFavorite && (
              <button type="button" className={css.clickHandler}>
                <Icon
                  name="BorderHeart-colored"
                  alt="مفضلة"
                  width={25}
                  height={25}
                />
              </button>
            )}
          </div>

          <div className={css.content}>
            {horizontal ? null : <h4>{listing.title}</h4>}

            <p className={css.price}>
              {range_price || original_range_price
                ? financialFormat(range_price || original_range_price, "ريال")
                : period}
            </p>

            {!!specs?.length && (
              <div className={css.specs}>
                {specs.map(({ icon, value }) => (
                  <div className={css.spec} key={`${icon}-${value}`}>
                    {icon && (
                      <Icon
                        priority
                        name={icon}
                        alt={icon}
                        width={20}
                        height={20}
                      />
                    )}
                    {value}
                  </div>
                ))}
              </div>
            )}

            {horizontal ? (
              <div className={css.description}>
                <p>
                  <small>{listing.title}</small>
                </p>
              </div>
            ) : (
              <>
                {content && (
                  <div className={css.description}>
                    <p>{content}</p>
                  </div>
                )}
              </>
            )}

            <div className={css.footer}>
              <p>
                {city}
                {district ? ` -${district}` : ""}
              </p>
            </div>
          </div>
          <div
            ref={imageWrapper}
            className={css.imageWrapper + " " + imageClass}
          >
            <Image
              loader={CloudfrontImageLoader}
              src={image || "villa-default.png"}
              onError={() => {
                const reportedImgs = getStoredImgsReportedIDs();
                if (!(reportedImgs && reportedImgs[image]) && token) {
                  addImgReportedID(image);
                  reportImagesIssue({
                    variables: {
                      listing_id: id,
                      images: image,
                    },
                  });
                }
              }}
              fill
              alt={title || ""}
              style={{
                objectFit: "cover",
              }}
              sizes="(max-width: 640px) 100px,(max-width: 1024px) 640px,750px"
              priority={preloadImage}
            />
            <div className={css.dailyFloating}>
              <Icon width={15} height={15} name="instant-booking" />
              <span>حجز يومي</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
export default DailyListingCard;
